import {
	useEffect, ChangeEvent, memo, useState, useRef
} from 'react';
import { NumberField } from '@components';
import { debounce } from 'lodash';
import { InputFieldProps } from '@components/FORM/InputField/InputField';
import { removeLeadingZeros } from '@utils';

export const MemoizedNumberField = memo((props: InputFieldProps) => {
	const { name, value, onChange, ...otherProps } = props;
	const [localValue, setLocalValue] = useState(value);
	const onChangeRef = useRef(onChange);

	const debouncedOnChange = useRef(
		debounce((val: string) => {
			const syntheticEvent = { target: { name, value: val } } as unknown as ChangeEvent<HTMLInputElement>;

			if ((val && typeof Number(val) === 'number') || val === '') {
				onChangeRef.current(syntheticEvent);
			}
		}, 300)
	).current;

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const newVal = e.target.value;
		setLocalValue(newVal);
		debouncedOnChange(newVal);
	};

	const handleOnBlur = (e: ChangeEvent<HTMLInputElement>) => {
		let newValue = e.target.value;

		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			newValue = removeLeadingZeros(newValue);
		}

		if (newValue.startsWith('.')) {
			newValue = `0${newValue}`;
		}

		if (newValue !== localValue) {
			setLocalValue(newValue);
			// Flush pending debounce to avoid stale update
			debouncedOnChange.cancel();

			onChange({ target: { name, value: newValue } } as unknown as ChangeEvent<HTMLInputElement>);
		}
	};

	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	useEffect(() => {
		if (value !== localValue) {
			setLocalValue(value);
		}
	}, [value]);

	useEffect(() => {
		return () => {
			debouncedOnChange.cancel();
		};
	}, []);

	return (
		<NumberField
			value={localValue}
			onChange={handleChange}
			name={name}
			keepErrorSpace={false}
			placeholder='Enter value'
			onBlur={handleOnBlur}
			{...otherProps}
		/>
	);
});